import { ref, type Ref } from 'vue';
import type { Router } from 'vue-router';
import { Size } from '@/ptfm/themes/models/enums';
import { SettingUrlType } from '@/ptfm/apps/models/enums';
import type { INavigationHelper, ModalEntry, FormNavParams, PopupNavParams, AppNavParams, DrillNavParams, IClickAction } from '../INavigationHelper';

/**
 * NavigationHelper for the 3cap theme.
 * Mirrors the default theme's behavior — separate singleton instance so theme
 * switches don't share modal stacks.
 */
export class NavigationHelper implements INavigationHelper {

  private router: Router | null = null;
  private _modalStack: Ref<ModalEntry[]> = ref([]);
  private _loading: Ref<boolean> = ref(false);

  get modalStack() { return this._modalStack; }
  get loading() { return this._loading; }
  get hasOpenModals() { return this._modalStack.value.length > 0; }

  init(router: Router) {
    this.router = router;
  }

  private getRouter(): Router {
    if (!this.router) {
      throw new Error('NavigationHelper: Router not initialized. Call init(router) first.');
    }
    return this.router;
  }

  showLoading() { this._loading.value = true; }
  hideLoading() { this._loading.value = false; }

  loadForm(params: FormNavParams) {
    const isFilePath = !!params.appPath;
    const size = params.size ?? (isFilePath ? Size.Popin : Size.Auto);
    const idKey = isFilePath ? params.appPath : params.formId;

    this.pushModal({
      id: `form_${idKey}_${Date.now()}`,
      type: 'form',
      size,
      formId: params.formId,
      configId: params.configId,
      pkId: params.pkId,
      appPath: params.appPath,
      appParams: params.appParams,
      onClose: params.callback,
    });
  }

  openPopup(params: PopupNavParams) {
    const size = params.size ?? Size.Large;

    this.pushModal({
      id: `popup_${Date.now()}`,
      type: 'popup',
      size,
      route: params.route,
      onClose: params.callback ? () => params.callback!('') : undefined,
    });
  }

  loadApp(params: AppNavParams) {
    const size = params.size ?? Size.Popin;

    this.pushModal({
      id: `app_${params.path}_${Date.now()}`,
      type: 'app',
      size,
      appPath: params.path,
      appParams: params.params || {},
      onClose: params.callback,
    });
  }

  openDrill(params: DrillNavParams) {
    this.loadApp({
      path: params.reportPath,
      params: {
        ...(params.defaultFilters || {}),
        title: params.title,
      },
      size: params.size ?? Size.Large,
      callback: params.callback,
    });
  }

  loadDetailPopup(recordId: number, installedAppId: number, callback?: () => void) {
    this.pushModal({
      id: `detail_${recordId}_${Date.now()}`,
      type: 'detail',
      size: Size.Large,
      // Detail popups identify a record by its primary key; reuse pkId so
      // ModalEntry stays unified with the form chain.
      pkId: recordId,
      installedAppId,
      onClose: callback,
    });
  }

  loadList(installedAppId: number) {
    const router = this.getRouter();
    router.push({ path: `/apps/${installedAppId}/list` });
  }

  closePopup() {
    const entry = this._modalStack.value.pop();
    if (entry?.onClose) entry.onClose();
  }

  closeAllModals() {
    for (const entry of this._modalStack.value) {
      if (entry.onClose) entry.onClose();
    }
    this._modalStack.value = [];
  }

  close() {
    if (this.hasOpenModals) this.closePopup();
  }

  handleConfigAction(action: IClickAction): boolean {
    const urlType = action.settingUrlType;

    switch (urlType) {
      case SettingUrlType.Form:
        if (action.settingUrl) {
          this.loadForm({
            appPath: action.settingUrl,
            appParams: action.params,
            size: action.size ?? this.mapFormSize(action.formSize),
          });
        } else {
          this.loadForm({
            formId: action.formId || 0,
            pkId: action.pkId || 0,
            configId: action.configId || 0,
            size: action.size ?? this.mapFormSize(action.formSize),
          });
        }
        return true;

      case SettingUrlType.Html:
        return false;

      case SettingUrlType.JsFunction:
        if (action.jsFunction) {
          try {
            const fn = new Function(action.jsFunction);
            fn();
          } catch (e) {
            console.error('NavigationHelper: Failed to execute jsFunction', action.jsFunction, e);
          }
          return true;
        }
        return false;

      case SettingUrlType.Delete:
        return false;

      case SettingUrlType.Component:
        if (action.settingUrl) {
          this.getRouter().push(action.settingUrl);
          return true;
        }
        return false;

      default:
        return false;
    }
  }

  mapFormSize(formSize?: number): Size {
    switch (formSize) {
      case 1: return Size.Mini;
      case 2: return Size.Medium;
      case 3: return Size.Large;
      case 4: return Size.ExtraLarge;
      case 5: return Size.Auto;
      case 6: return Size.Popin;
      default: return Size.Popin;
    }
  }

  getSizeClass(size: Size): string {
    switch (size) {
      case Size.Mini: return 'w-1/3 max-w-sm';
      case Size.Medium: return 'w-1/2 max-w-2xl';
      case Size.Large: return 'w-2/3 max-w-4xl';
      case Size.ExtraLarge: return 'w-full max-w-7xl';
      case Size.Popin: return 'h-full w-full';
      default: return 'w-2/3 max-w-4xl';
    }
  }

  isModalSize(size: Size): boolean {
    return size === Size.Mini || size === Size.Medium ||
           size === Size.Large || size === Size.ExtraLarge;
  }

  isDockedSize(_size: Size): boolean { return false; }
  isPopinSize(size: Size): boolean { return size === Size.Popin; }

  getDirection(): 'ltr' | 'rtl' {
    const appRoot = document.getElementById('app-root') || document.getElementById('app');
    const dir = appRoot?.getAttribute('dir')
      || document.documentElement.getAttribute('dir')
      || 'ltr';
    return dir === 'rtl' ? 'rtl' : 'ltr';
  }

  isRtl(): boolean { return this.getDirection() === 'rtl'; }

  private pushModal(entry: ModalEntry) {
    this._modalStack.value.push(entry);
  }

  getLastModalId(): string | undefined {
    const last = this._modalStack.value[this._modalStack.value.length - 1];
    return last ? last.id : undefined;
  }

  private static _instance: NavigationHelper;
  static getInstance(): NavigationHelper {
    if (!NavigationHelper._instance) {
      NavigationHelper._instance = new NavigationHelper();
    }
    return NavigationHelper._instance;
  }
}
